import * as THREE from 'three';

export class MapTextures {
  /**
   * Industrial modular metal floor plate with rivets and non-slip treads
   */
  public static createFloorGrateTexture(): THREE.CanvasTexture {
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 512;
    const ctx = canvas.getContext('2d')!;

    // Base industrial slate
    ctx.fillStyle = '#3a4659';
    ctx.fillRect(0, 0, 512, 512);

    const tileSize = 128;
    for (let x = 0; x < 512; x += tileSize) {
      for (let y = 0; y < 512; y += tileSize) {
        // Tile interior
        ctx.fillStyle = '#475569';
        ctx.fillRect(x + 4, y + 4, tileSize - 8, tileSize - 8);

        // Highlight top/left
        ctx.strokeStyle = '#64748b';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(x + 4, y + tileSize - 4);
        ctx.lineTo(x + 4, y + 4);
        ctx.lineTo(x + tileSize - 4, y + 4);
        ctx.stroke();

        // Shadow bottom/right
        ctx.strokeStyle = '#1e293b';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(x + tileSize - 4, y + 4);
        ctx.lineTo(x + tileSize - 4, y + tileSize - 4);
        ctx.lineTo(x + 4, y + tileSize - 4);
        ctx.stroke();

        // Non-slip diamond / diagonal treads in tile center
        ctx.strokeStyle = '#334155';
        ctx.lineWidth = 2;
        for (let i = 24; i < tileSize - 20; i += 16) {
          ctx.beginPath();
          ctx.moveTo(x + i, y + 20);
          ctx.lineTo(x + tileSize - 20, y + i);
          ctx.stroke();
        }

        // Corner steel rivets
        const rivetOffsets = [
          [12, 12],
          [tileSize - 12, 12],
          [12, tileSize - 12],
          [tileSize - 12, tileSize - 12],
        ];
        rivetOffsets.forEach(([rx, ry]) => {
          ctx.fillStyle = '#1e293b';
          ctx.beginPath();
          ctx.arc(x + rx, y + ry, 3.5, 0, Math.PI * 2);
          ctx.fill();

          ctx.fillStyle = '#94a3b8';
          ctx.beginPath();
          ctx.arc(x + rx - 1, y + ry - 1, 1.8, 0, Math.PI * 2);
          ctx.fill();
        });
      }
    }

    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.colorSpace = THREE.SRGBColorSpace;
    return texture;
  }

  /**
   * Iconic Helipad landing deck with tactical "H", circular guidance rings, and hazard chevrons
   */
  public static createHelipadTexture(): THREE.CanvasTexture {
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 512;
    const ctx = canvas.getContext('2d')!;

    // Dark tactical flight deck
    ctx.fillStyle = '#374151';
    ctx.fillRect(0, 0, 512, 512);

    const cx = 256;
    const cy = 256;

    // Segmented metal plates radiating from center
    ctx.strokeStyle = '#1f2937';
    ctx.lineWidth = 3;
    for (let i = 0; i < 8; i++) {
      const angle = (i * Math.PI) / 4;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(cx + Math.cos(angle) * 256, cy + Math.sin(angle) * 256);
      ctx.stroke();
    }

    // Outer hazard chevron ring
    const outerR = 236;
    ctx.strokeStyle = '#eab308';
    ctx.lineWidth = 14;
    ctx.beginPath();
    ctx.arc(cx, cy, outerR, 0, Math.PI * 2);
    ctx.stroke();

    // Inner dashed guidance ring
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 4;
    ctx.setLineDash([16, 12]);
    ctx.beginPath();
    ctx.arc(cx, cy, 180, 0, Math.PI * 2);
    ctx.stroke();
    ctx.setLineDash([]);

    // Solid inner ring
    ctx.strokeStyle = '#eab308';
    ctx.lineWidth = 6;
    ctx.beginPath();
    ctx.arc(cx, cy, 140, 0, Math.PI * 2);
    ctx.stroke();

    // Bold Tactical "H" in center
    ctx.fillStyle = '#f59e0b';
    ctx.shadowColor = 'rgba(0,0,0,0.8)';
    ctx.shadowBlur = 10;

    // Left bar
    ctx.fillRect(cx - 75, cy - 80, 28, 160);
    // Right bar
    ctx.fillRect(cx + 47, cy - 80, 28, 160);
    // Crossbar
    ctx.fillRect(cx - 75, cy - 16, 150, 32);

    ctx.shadowBlur = 0;

    // Technical landing zone coordinates text
    ctx.font = 'bold 16px monospace';
    ctx.fillStyle = '#94a3b8';
    ctx.textAlign = 'center';
    ctx.fillText('LANDING PAD 01 // SECTOR MORPHEUS', cx, cy + 115);

    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    return texture;
  }

  /**
   * Modular futuristic skyscraper facade with titanium panels, seams, and technical vents
   */
  public static createSkyscraperWallTexture(): THREE.CanvasTexture {
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 512;
    const ctx = canvas.getContext('2d')!;

    // Titanium plate base
    ctx.fillStyle = '#334155';
    ctx.fillRect(0, 0, 512, 512);

    const pw = 256;
    const ph = 128;

    for (let x = 0; x < 512; x += pw) {
      for (let y = 0; y < 512; y += ph) {
        // Panel face
        ctx.fillStyle = (x + y) % 256 === 0 ? '#3f4c60' : '#384556';
        ctx.fillRect(x + 2, y + 2, pw - 4, ph - 4);

        // Highlight
        ctx.strokeStyle = '#64748b';
        ctx.lineWidth = 1.5;
        ctx.strokeRect(x + 2, y + 2, pw - 4, ph - 4);

        // Recessed ventilation grill
        ctx.fillStyle = '#0f172a';
        ctx.fillRect(x + 24, y + 36, pw - 48, ph - 72);

        ctx.strokeStyle = '#475569';
        ctx.lineWidth = 2;
        for (let vy = y + 42; vy < y + ph - 40; vy += 6) {
          ctx.beginPath();
          ctx.moveTo(x + 28, vy);
          ctx.lineTo(x + pw - 28, vy);
          ctx.stroke();
        }

        // Seam rivets
        ctx.fillStyle = '#64748b';
        ctx.fillRect(x + 8, y + 8, 3, 3);
        ctx.fillRect(x + pw - 11, y + 8, 3, 3);
        ctx.fillRect(x + 8, y + ph - 11, 3, 3);
        ctx.fillRect(x + pw - 11, y + ph - 11, 3, 3);
      }
    }

    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.colorSpace = THREE.SRGBColorSpace;
    return texture;
  }

  /**
   * High-contrast black and yellow 45-degree diagonal hazard caution stripes
   */
  public static createHazardStripeTexture(): THREE.CanvasTexture {
    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 256;
    const ctx = canvas.getContext('2d')!;

    // Yellow background
    ctx.fillStyle = '#eab308';
    ctx.fillRect(0, 0, 256, 256);

    // Diagonal black stripes
    ctx.fillStyle = '#0f172a';
    const stripeWidth = 32;

    for (let x = -256; x < 512; x += stripeWidth * 2) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x + stripeWidth, 0);
      ctx.lineTo(x + stripeWidth + 256, 256);
      ctx.lineTo(x + 256, 256);
      ctx.closePath();
      ctx.fill();
    }

    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.colorSpace = THREE.SRGBColorSpace;
    return texture;
  }

  /**
   * Catwalk / Skybridge steel grating with perforated diamond mesh and guide tracks
   */
  public static createSkybridgeTexture(): THREE.CanvasTexture {
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 256;
    const ctx = canvas.getContext('2d')!;

    // Metal floor
    ctx.fillStyle = '#334155';
    ctx.fillRect(0, 0, 512, 256);

    // Cyan glowing guide rails on top and bottom borders
    ctx.fillStyle = '#00fff2';
    ctx.fillRect(0, 0, 512, 12);
    ctx.fillRect(0, 244, 512, 12);

    // Inner yellow hazard stripes near edges
    ctx.fillStyle = '#eab308';
    ctx.fillRect(0, 16, 512, 6);
    ctx.fillRect(0, 234, 512, 6);

    // Diamond perforated steel walking grid in center
    ctx.strokeStyle = '#1e293b';
    ctx.lineWidth = 2;
    for (let x = 0; x < 512; x += 16) {
      for (let y = 32; y < 224; y += 16) {
        ctx.fillStyle = '#1e293b';
        ctx.beginPath();
        ctx.arc(x + 8, y + 8, 3, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // Transverse steel support ridges
    ctx.fillStyle = '#475569';
    for (let x = 0; x < 512; x += 64) {
      ctx.fillRect(x, 26, 6, 204);
    }

    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.colorSpace = THREE.SRGBColorSpace;
    return texture;
  }
}
